import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useState } from 'react';
import style from './index.module.css';
import cx from 'clsx';
import { useHistory } from 'react-router-dom';
import { OTPInput } from '@/components/OTPInput';
import Checkbox from 'antd/es/checkbox';

type PasswordInputProps = {
  className?: string;
  passwordInputRef: React.RefObject<HTMLInputElement>;
  onPasswordChange?: (value: string) => void;
  setSkipPassword?: (value: boolean) => void;
  showError?: boolean;
  errorMessage?: string;
  showInputTips?: boolean;
  errorType?: PasswordErrorType;
};

export enum PasswordErrorType {
  WRONG_PASSWORD = 1,
  VERIFY_ERROR = 2,
}

export const PasswordInput = ({
  className,
  passwordInputRef,
  onPasswordChange,
  setSkipPassword,
  showError,
  showInputTips,
  errorMessage,
  errorType,
}: PasswordInputProps) => {
  const { t } = useTranslation();

  const history = useHistory();
  const [skipPassword, _setSkipPassword] = useState(false);
  const onChange = useCallback(
    value => {
      onPasswordChange && onPasswordChange(value);
    },
    [onPasswordChange],
  );

  const onSettingSkipPassword = useCallback(
    (skip: boolean) => {
      setSkipPassword && setSkipPassword(skip);
      _setSkipPassword(skip);
    },
    [setSkipPassword],
  );

  const redirectToAccount = useCallback(
    (targetModal: string) => {
      history.push('/account?modal=' + targetModal);
    },
    [history],
  );

  const ErrorMessageComponent = useMemo(() => {
    if (!errorType) return null;
    switch (errorType) {
      case PasswordErrorType.WRONG_PASSWORD:
        return <>{t('PasswordInput.upn8tg')}</>;
      case PasswordErrorType.VERIFY_ERROR:
        return (
          <>
            {t('PasswordInput.1pvk5ti')}{' '}
            <span className={style.resetKey} onClick={() => redirectToAccount('reset')}>
              {t('PasswordInput.1ul3366')}
            </span>
          </>
        );
      default:
        return null;
    }
  }, [errorType, redirectToAccount, t]);

  return (
    <div className={cx(style.passwordPage, className)}>
      {showInputTips ? null : <span className={style.enterTips}>{t('PasswordInput.t35tog')}</span>}
      <div className={style.inputContainer}>
        <OTPInput
          type="password"
          inputRef={passwordInputRef}
          onChange={onChange}
          autoFocus
          forceFocus={true}
        />
      </div>
      {showInputTips ? (
        <div className={style.bottomTips}>{t('PasswordInput.x44xvu')}</div>
      ) : (
        <div className={style.forgetPassword} onClick={() => redirectToAccount('forgetPassword')}>
          {t('PasswordInput.lxactj')}
        </div>
      )}
      {showError ? (
        <div className={style.incorrectPassword}>
          {ErrorMessageComponent || t('PasswordInput.incorrect')}
        </div>
      ) : null}

      {setSkipPassword ? (
        <div className={style.optionBottom} onClick={() => onSettingSkipPassword(!skipPassword)}>
          <Checkbox className={style.checkbox} checked={skipPassword} />
          {t('PasswordInput.skip')}
        </div>
      ) : null}
    </div>
  );
};
